import React, { useReducer, useContext, useRef, useEffect, useState } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { subCategoryReducer } from './subCategoryReducer';
import { SubCategoryActionTypes } from './subCategoryActions';
import {
	subCategoryInitialState,
	SubCategoryContext,
	TSortBy,
	TCountOfItems,
	IFilterValues,
	TTypeOfModalWindow,
} from './subCategoryContext';
import { useFetchData } from 'hooks/useFetchData';
import { ISubCategory, IGetPaginatedSubCategories } from 'types';
import { AxiosResponse } from 'axios';
import { getPaginatedSubCategories, deleteSubCategory } from 'backend/subcategories';

const SubCategoryState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(subCategoryReducer, subCategoryInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<ISubCategory>, IGetPaginatedSubCategories>(
		isMounted.current ? getPaginatedSubCategories : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			categoryId: state.IDSelectedCategory,
			isHidden: state.subCategoryIsHidden,
			sortByCustomer: state.sortBy === 'CustomerA-Z' || state.sortBy === 'CustomerZ-A' ? state.sortBy : null,
			sortByCategories:
				state.sortBy === 'CategoriesA-Z' || state.sortBy === 'CategoriesZ-A' ? state.sortBy : null,
			sortBySubCategories:
				state.sortBy === 'SubCategoriesA-Z' || state.sortBy === 'SubCategoriesZ-A' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: SubCategoryActionTypes.SET_SUBCATEGORIES,
				payload: {
					subCategories: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = async () => {
		try {
			showLoader();
			await deleteSubCategory(state.IDForDeletion);
			const filteredSubcategories = state.subCategories.subCategories.filter(
				(el) => el.id !== state.IDForDeletion,
			);
			dispatch({
				type: SubCategoryActionTypes.SET_SUBCATEGORIES,
				payload: {
					subCategories: {
						subCategoriesCount: state.subCategories.subCategoriesCount - 1,
						subCategories: filteredSubcategories,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: SubCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	};

	const changeSearchString = (value: string) => {
		if (allowChangingSearchString) {
			dispatch({
				type: SubCategoryActionTypes.CHANGE_SEARCH_STRING,
				payload: {
					searchString: value,
				},
			});
		}
	};

	const setIDCustomer = (value: Nullable<string>) => {
		dispatch({
			type: SubCategoryActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value ? value : null,
			},
		});
	};

	const setIDRestaurant = (value: Nullable<string>) => {
		dispatch({
			type: SubCategoryActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value ? value : null,
			},
		});
	};

	const setIDCategory = (value: Nullable<string>) => {
		dispatch({
			type: SubCategoryActionTypes.SET_ID_CATEGORY,
			payload: {
				IDSelectedCategory: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: SubCategoryActionTypes.RESET_SELECTED_CONTROLS });

	const setSortBy = (sortBy: TSortBy) => {
		dispatch({
			type: SubCategoryActionTypes.SET_SORT_BY,
			payload: {
				sortBy: sortBy,
			},
		});
	};

	const setCountOfItems = (count: TCountOfItems) => {
		dispatch({
			type: SubCategoryActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: count,
			},
		});
	};

	const toggleSubCategoryIsHidden = () => {
		dispatch({ type: SubCategoryActionTypes.TOGGLE_SUB_CATEGORY_IS_HIDDEN });
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: SubCategoryActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const setIDForDeletion = (value: string) => {
		dispatch({
			type: SubCategoryActionTypes.SET_ID_FOR_DELETION,
			payload: {
				IDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: SubCategoryActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const setFiltersFromQueryString = (value: IFilterValues) => {
		dispatch({
			type: SubCategoryActionTypes.SET_FILTERS_FROM_QUERY_STRING,
			payload: {
				filters: value,
			},
		});
		isMounted.current = true;
		refetch();
	};

	return (
		<SubCategoryContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDCustomer,
				setIDRestaurant,
				setIDCategory,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				toggleSubCategoryIsHidden,
				changeCurrentPage,
				setIDForDeletion,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				subCategories: state.subCategories,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				IDSelectedCategory: state.IDSelectedCategory,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				subCategoryIsHidden: state.subCategoryIsHidden,
				currentPage: state.currentPage,
				IDForDeletion: state.IDForDeletion,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</SubCategoryContext.Provider>
	);
};

export default SubCategoryState;
